







































































































































































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  CalendarUnitTypeView,
  CalendarRatePlanAccomodationUnitTypeView,
  CalendarUnitView,
  MaintenanceTeamMember,
  RelatedRatePlansPriceUpdateTypeEnum,
  RelatedRatePlansRestrictionsUpdateTypeEnum,
} from "./CalendarTypes";
import { DateTime } from "luxon";
import DateField from "@/components/misc/DateField.vue";
import _ from "lodash";
import { Action, Getter } from "vuex-class";
import { Currency } from "@/shared/models/currency";
import { AccomodationUnitStatus } from "@/shared/models/accomodationUnitStatus";
import NumericPicker from "@/components/misc/NumericPicker.vue";
import {
  ContingentRatePlanDataView,
  ContingentRatePlanView,
  ContingentRowIdentifier,
} from "../contingent/ContingentTypes";
import {
  parseLocaleNumber,
  formatLocaleNumber,
} from "@/components/numbers/numberFormatUtils";

export type CalendarEditorMode =
  | "price"
  | "inputPrice"
  | "numberRestriction"
  | "booleanRestriction"
  | "quantity"
  | "unitStatus"
  | "contingentPriceMonthYear"
  | "contingentPriceWeek";

@Component({ components: { NumericPicker, DateField } })
export default class CalendarEditor extends Vue {
  @Prop() isMobile?: boolean;

  key = 0;
  menu = false;
  form = false;
  submitting = false;
  mode: CalendarEditorMode = "quantity";

  unitType: CalendarUnitTypeView | null = null;
  unit: CalendarUnitView | null = null;
  ratePlan:
    | CalendarRatePlanAccomodationUnitTypeView
    | ContingentRatePlanView
    | null = null;
  field: {
    key: string;
    type: "number" | "boolean";
    textResource: string;
  } | null = null;

  from = DateTime.local();
  to = DateTime.local();

  hasOBRules = false;
  hasDerived = false;

  RelatedRatePlansPriceUpdateTypeEnum = RelatedRatePlansPriceUpdateTypeEnum;
  RelatedRatePlansRestrictionsUpdateTypeEnum =
    RelatedRatePlansRestrictionsUpdateTypeEnum;

  relatedRatePlansPriceUpdateType =
    RelatedRatePlansPriceUpdateTypeEnum.ApplyOBLOSCategories;
  relatedRatePlansRestrictionsUpdateType =
    RelatedRatePlansRestrictionsUpdateTypeEnum.DontUpdate;

  pos: { x: number; y: number } = { x: 0, y: 0 };
  originalValue: any = null;

  contingentSelectedRows: ContingentRowIdentifier[] | null = null;

  closeable = false;

  get contingentMode() {
    return (
      this.mode === "contingentPriceMonthYear" ||
      this.mode === "contingentPriceWeek"
    );
  }

  get nights() {
    return this.to.diff(this.from, "days").days + 1;
  }

  get fromUI() {
    return this.from.toString();
  }

  set fromUI(val) {
    this.from = DateTime.fromISO(val);
  }

  get toUI() {
    return this.to.toString();
  }

  set toUI(val) {
    this.to = DateTime.fromISO(val);
  }

  priceRule = (v: string) => !v || /^(\+|-)?([\d,.]+)(%)?$/.test(v);
  quantityRule = (v: string) => !v || /^\s*(\+|-)?([^+-]+)$/.test(v);

  mustBePositive(v: any) {
    return +v >= 0 ? true : this.$t("Validation.MustBePositive");
  }

  maxDecimals(value: any) {
    if (value !== undefined) {
      //remove '%' so it's not counted as a number for max decimal check
      if (value.toString().slice(-1) === "%") {
        value = value.toString().slice(0, -1);
      }      
      return !(this.countDecimals(value) > 2)
        ? true
        : this.$t("Validation.MaxDecimals{max}", {
            max: 2,
          });
    } else {
      return true;
    }
  }

  get decimalSeparator() {
    const separator = Intl.NumberFormat(this.internalLanguage)
      .format(1.1)
      .replace(/\p{Number}/gu, "");
    return separator;
  }

  countDecimals(num: number) {
    return num.toString().split(this.decimalSeparator)[1]?.length || 0;
  }

  get subtitle() {
    if (this.from && this.to) {
      if (this.from.toISODate() !== this.to.toISODate()) {
        return this.$t("From {from} to {to}", {
          from: this.$d(this.from.toJSDate()),
          to: this.$d(this.to.toJSDate()),
        });
      } else {
        return this.$t("On {date}", {
          date: this.$d(this.from.toJSDate()),
        });
      }
    }

    return "";
  }

  public open(
    unitType: CalendarUnitTypeView | null,
    unit: CalendarUnitView | null,
    ratePlan:
      | CalendarRatePlanAccomodationUnitTypeView
      | ContingentRatePlanView
      | null,
    field: {
      key: string;
      type: "number" | "boolean";
      textResource: string;
    } | null,
    mode: CalendarEditorMode,
    from: DateTime,
    to: DateTime,
    pos: { x: number; y: number },
    originalValue: any,
    contingentSelectedRows: ContingentRowIdentifier[] | null,
    hasOBRules: boolean,
    hasDerived: boolean
  ) {
    this.key++;
    this.mode = mode;

    this.unitType = unitType;
    this.unit = unit;
    this.ratePlan = ratePlan;
    this.field = field;

    this.from = from;
    this.to = to;

    this.hasOBRules = hasOBRules;
    this.hasDerived = hasDerived;

    this.pos = pos;
    this.originalValue = originalValue;

    this.contingentSelectedRows = contingentSelectedRows;

    if (
      this.mode === "price" ||
      this.mode === "inputPrice" ||
      this.contingentMode
    ) {
      this.priceInput = formatLocaleNumber(
        this.originalValue,
        2,
        this.internalLanguage
      );
    } else if (this.mode === "booleanRestriction") {
      this.booleanInput = this.originalValue;
    } else if (this.mode === "quantity") {
      this.quantityInput = this.originalValue;
    } else if (this.mode === "unitStatus") {
      this.unitStatusInput = this.originalValue.statusId;
      this.unitMaintenanceAssignedToTenantUserId =
        this.originalValue.tenantUserId;
      this.textInput = unit?.note || "";
    } else {
      this.numberInput = this.originalValue;
    }

    if (
      (this.mode === "price" || this.mode === "inputPrice") &&
      !this.contingentMode
    ) {
      // don't mess with restrictions when updating prices
      this.relatedRatePlansRestrictionsUpdateType =
        RelatedRatePlansRestrictionsUpdateTypeEnum.DontUpdate;

      // non-OB tenants should always apply the change to OB rates
      if (this.tenantOBRsUseRelativePricing) {
        this.relatedRatePlansPriceUpdateType =
          RelatedRatePlansPriceUpdateTypeEnum.ApplyChange;
      }
      // for others get the default
      else {
        this.relatedRatePlansPriceUpdateType =
          this.calendarOBLOSCategoriesApplicationAllowed &&
          this.calendarRelatedRatesPriceUpdateTypeDefault
            ? this.calendarRelatedRatesPriceUpdateTypeDefault ??
              RelatedRatePlansPriceUpdateTypeEnum.DontUpdate
            : RelatedRatePlansPriceUpdateTypeEnum.DontUpdate;
      }
    } else if (!this.contingentMode) {
      // don't mess with price when updating restrictions
      this.relatedRatePlansPriceUpdateType =
        RelatedRatePlansPriceUpdateTypeEnum.DontUpdate;

      // get the default for restrictions
      this.relatedRatePlansRestrictionsUpdateType =
        this.calendarRelatedRatesRestrictionsUpdateTypeDefault ??
        RelatedRatePlansRestrictionsUpdateTypeEnum.DontUpdate;
    }

    this.menu = true;
    this.closeable = false;
    setTimeout(() => {
      // this is a hack, otherwise, it's closed immediately for some
      // bizzare reason on vuetify's side
      this.closeable = true;
    }, 350);
  }

  quantityInput: string | null = null;
  numberInput: number | null = null;
  booleanInput: boolean | null = null;
  priceInput: string | null = null;
  unitStatusInput: number | null = null;
  unitMaintenanceAssignedToTenantUserId: number | null = null;
  textInput: string | null = null;

  // prettier-ignore
  @Action("codebooks/refreshCurrencies") refreshCurrencies?: () => Promise<void>;
  @Getter("codebooks/currencies") currencies?: Currency[];

  @Action("codebooks/refreshAccomodationUnitStatuses")
  refreshAccomodationUnitStatuses?: () => Promise<void>;
  @Getter("codebooks/accomodationUnitStatuses")
  accomodationUnitStatuses?: AccomodationUnitStatus[];

  @Getter("maintenance/maintenanceTeam")
  maintenanceTeam?: MaintenanceTeamMember[];
  @Action("maintenance/refreshMaintenanceTeam")
  refreshMaintenanceTeam?: () => Promise<void>;

  @Getter("appParams/tenantOBRsUseRelativePricing")
  tenantOBRsUseRelativePricing?: boolean;

  @Getter("appParams/calendarOBLOSCategoriesApplicationAllowed")
  calendarOBLOSCategoriesApplicationAllowed?: boolean;
  @Getter("appParams/calendarRelatedRatesPriceUpdateTypeDefault")
  calendarRelatedRatesPriceUpdateTypeDefault?: number;
  @Getter("appParams/calendarRelatedRatesRestrictionsUpdateTypeDefault")
  calendarRelatedRatesRestrictionsUpdateTypeDefault?: number;

  @Action("calendar/startBlockedAvailabilitiesCheckForUnitTypes")
  startBlockedAvailabilitiesCheckForUnitTypes?: (
    payload: number[]
  ) => Promise<void>;

  async created() {
    if ((this.currencies?.length ?? 0) === 0 && this.refreshCurrencies) {
      this.refreshCurrencies();
    }

    if (
      (this.accomodationUnitStatuses?.length ?? 0) === 0 &&
      this.refreshAccomodationUnitStatuses
    ) {
      this.refreshAccomodationUnitStatuses();
    }

    if (
      (this.maintenanceTeam?.length ?? 0) === 0 &&
      this.refreshMaintenanceTeam
    ) {
      this.refreshMaintenanceTeam();
    }
  }

  @Getter("appParams/workingCurrency") workingCurrency?: Currency;
  @Getter("language/language") language?: string;

  get internalLanguage() {
    return this.language ?? "hr-HR";
  }

  get price() {
    if (
      this.mode === "price" ||
      this.mode === "inputPrice" ||
      this.contingentMode
    ) {
      const original = !_.isNumber(this.originalValue)
        ? 0
        : +this.originalValue;

      const groups = this.priceInput
        ? /^(\+|-)?([\d,.]+)(%)?$/.exec(this.priceInput ?? "")
        : ["", "", "0", ""]; // default to 0;

      if (groups) {
        const flat = [...groups];
        const percentage = flat[3] === "%";
        const subtraction = flat[1] === "-";
        const addition = flat[1] === "+";
        const number = parseLocaleNumber(flat[2], this.internalLanguage);

        let total = original;
        let tr = "";

        if (percentage) {
          if (addition) {
            total += (original * number) / 100;
            tr = "Calendar.PriceEditor.Percentage.Add";
          } else if (subtraction) {
            total -= (original * number) / 100;
            tr = "Calendar.PriceEditor.Percentage.Sub";
          } else {
            total = (original * number) / 100;
            tr = "Calendar.PriceEditor.Percentage.Set";
          }
        } else {
          if (addition) {
            total += number;
            tr = "Calendar.PriceEditor.Absolute.Add";
          } else if (subtraction) {
            total -= number;
            tr = "Calendar.PriceEditor.Absolute.Sub";
          } else {
            total = number;
            tr = "Calendar.PriceEditor.Absolute.Set";
          }
        }

        let valueCurrency = number.toString();
        if (this.ratePlan) {
          try {
            // prettier-ignore
            valueCurrency = this.$n(number, { style: "currency", currency: this.ratePlan.currencyCode, currencyDisplay: "code" })
          } catch {
            /* ignore */
          }
        }

        const res = {
          value: number,
          total,
          valueCurrency,
          percentage,
          addition,
          subtraction,
        };

        return {
          ...res,
          valid: true,
          text: this.$t(tr, res),
        };
      }
    }

    return {
      valid: false,
      value: this.originalValue,
      total: this.originalValue,
      percentage: false,
      addition: false,
      subtraction: false,
      text: this.$t("Calendar.PriceEditor.Invalid"),
    };
  }

  public close() {
    this.menu = false;
    this.$emit("closed");
  }

  @Watch("menu")
  menuChanged(fresh: boolean, old?: boolean) {
    if (fresh !== old) {
      this.$emit(fresh ? "open" : "closed");
    }
  }

  private submit() {
    if (this.mode === "price") {
      this.submitPrice();
    } else if (this.contingentMode) {
      this.submitContingentPrice();
    } else if (this.mode === "quantity") {
      this.submitQuantity();
    } else if (this.mode === "inputPrice") {
      this.submitInputPrice();
    } else if (this.mode === "unitStatus") {
      this.submitUnitStatus();
    } else {
      this.submitRestriction();
    }
  }

  private submitQuantity() {
    const val = this.quantityInput ?? "";

    const extracted = /^\s*(\+|-)?([^+-]+)$/.exec(val);

    let [prefix, numeric] = ["+", 0];
    if (extracted) {
      prefix = extracted[1] ?? "";
      numeric = extracted[2] ? +extracted[2] : 0;
    }

    this.submitting = true;
    this.axios
      .put("/calendar/SetUnitTypeQuantity", {
        unitTypeId: this.unitType?.id,
        from: this.from?.toISODate()?.substr(0, 10),
        to: this.to?.toISODate()?.substr(0, 10),
        value: +numeric,
        isSet: prefix === "",
        addition: prefix === "+",
        subtraction: prefix === "-",
      })
      .then((res) => {
        if (res && res.data && res.data.asyncBulkEditStarted) {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: this.$t("Async bulk edit started"),
            timeout: 30000,
          });
        } else {
          this.$emit("calendarReload", {
            from: this.from,
            to: this.to,
          });
        }
        this.close();
      })
      .finally(() => (this.submitting = false));
  }
  private submitPrice() {
    this.submitting = true;
    this.axios
      .put("/calendar/AdjustAvailabilityPrice", {
        unitTypeId: this.unitType?.id,
        ratePlanId: this.ratePlan?.id,
        from: this.from?.toISODate()?.substr(0, 10),
        to: this.to?.toISODate()?.substr(0, 10),
        value: this.price.value,
        isAbsolute: !this.price.percentage,
        addition: this.price.addition,
        subtraction: this.price.subtraction,
        relatedRatePlansPriceUpdateType: this.relatedRatePlansPriceUpdateType,
        relatedRatePlansRestrictionsUpdateType:
          this.relatedRatePlansRestrictionsUpdateType,
      })
      .then((res) => {
        if (res && res.data && res.data.asyncBulkEditStarted) {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: this.$t("Async bulk edit started"),
            timeout: 30000,
          });
        } else {
          this.$emit("calendarReload", {
            from: this.from,
            to: this.to,
          });
        }
        this.close();
      })
      .finally(() => (this.submitting = false));
  }
  private submitRestriction() {
    const val = this.numberInput ? +this.numberInput : 0;
    this.submitting = true;
    this.axios
      .put("/calendar/SetAvailabilityRestriction", {
        unitTypeId: this.unitType?.id,
        ratePlanId: this.ratePlan?.id,
        from: this.from?.toISODate()?.substr(0, 10),
        to: this.to?.toISODate()?.substr(0, 10),
        field: this.field?.key,
        numberValue: this.mode === "numberRestriction" ? val : null,
        booleanValue:
          this.mode === "booleanRestriction" ? this.booleanInput : null,
        relatedRatePlansPriceUpdateType: this.relatedRatePlansPriceUpdateType,
        relatedRatePlansRestrictionsUpdateType:
          this.relatedRatePlansRestrictionsUpdateType,
      })
      .then((res) => {
        if (res && res.data && res.data.asyncBulkEditStarted) {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: this.$t("Async bulk edit started"),
            timeout: 30000,
          });
        } else {
          this.$emit("calendarReload", {
            from: this.from,
            to: this.to,
          });
        }
        this.close();
      })
      .finally(() => (this.submitting = false));
  }

  private submitInputPrice() {
    this.submitting = true;
    this.axios
      .put("/calendar/SetAvailabilityRestriction", {
        unitTypeId: this.unitType?.id,
        ratePlanId: this.ratePlan?.id,
        from: this.from?.toISODate()?.substr(0, 10),
        to: this.to?.toISODate()?.substr(0, 10),
        field: this.field?.key,
        numberValue: this.price.value,
        isAbsolute: !this.price.percentage,
        addition: this.price.addition,
        subtraction: this.price.subtraction,
        relatedRatePlansPriceUpdateType: this.relatedRatePlansPriceUpdateType,
        relatedRatePlansRestrictionsUpdateType:
          this.relatedRatePlansRestrictionsUpdateType,
      })
      .then((res) => {
        if (res && res.data && res.data.asyncBulkEditStarted) {
          this.$store.dispatch("showNotification", {
            color: "success",
            message: this.$t("Async bulk edit started"),
            timeout: 30000,
          });
        } else {
          this.$emit("calendarReload", {
            from: this.from,
            to: this.to,
          });
        }
        this.close();
      })
      .finally(() => (this.submitting = false));
  }

  private submitUnitStatus() {
    this.submitting = true;
    this.axios
      .put("/maintenance/AccomodationUnitStatusUpdate", {
        id: this.unit?.id,
        statusId: this.unitStatusInput,
        maintenanceAssignedToTenantUserId:
          this.unitMaintenanceAssignedToTenantUserId,
        calendarUnitRemark: this.textInput,
      })
      .then(() => {
        if (this.unit?.id) {
          if (this.unitStatusInput) {
            this.unit.statusId = this.unitStatusInput;
            this.unit.maintenanceAssignedToTenantUserId =
              this.unitMaintenanceAssignedToTenantUserId;
          }

          if (this.textInput != null && this.textInput != undefined) {
            this.unit.note = this.textInput;
          }
        }
        this.close();
      })
      .finally(() => (this.submitting = false));
  }

  getRowDates(month: number) {
    const year = this.from.year;
    const fromDate = DateTime.local(year, month, this.from.day);
    const toDate = DateTime.local(year, month, this.to.day);
    return [fromDate, toDate];
  }

  private submitContingentPrice() {
    if (this.contingentSelectedRows) {
      for (let index = 0; index < this.contingentSelectedRows.length; index++) {
        const element = this.contingentSelectedRows[index];
        const rowDates =
          this.mode === "contingentPriceMonthYear"
            ? this.getRowDates(element.month)
            : [this.from, this.to];
        if (element.ratePlan) {
          this.submitting = true;
          this.axios
            .put("/calendar/AdjustAvailabilityPrice", {
              unitTypeId: element.unitType,
              ratePlanId: element.ratePlan.id,
              from: rowDates[0].toISODate()?.substr(0, 10),
              to: rowDates[1].toISODate()?.substr(0, 10),
              value: this.price.value,
              isAbsolute: !this.price.percentage,
              addition: this.price.addition,
              subtraction: this.price.subtraction,
            })
            .then((res) => {
              if (res && res.data) {
                this.mergeContingentResponseData(res.data, element);
              }
              this.close();
            })
            .finally(() => (this.submitting = false));
        }
      }
    }
  }

  @Action("calendar/addAvailabilities") addAvailabilities?: (
    payload: any[]
  ) => void;

  private mergeResponseData(data: any[]) {
    if (this.unitType && data && data.length > 0 && this.addAvailabilities) {
      this.addAvailabilities(data);
    }
  }

  private mergeContingentResponseData(
    data: any[],
    element: ContingentRowIdentifier
  ) {
    if (element.ratePlan && data && data.length > 0) {
      for (const a of data) {
        if (a.ratePlanId == element.ratePlan.id) {
          const dayData = element.ratePlan.ratePlanData.find(
            (x) => x.date.substring(0, 10) == a.date.substring(0, 10)
          );
          if (dayData) {
            const originalPrice =
              dayData.price / (1 + dayData.basePriceDifference / 100);
            dayData.price = a.price;
            dayData.basePriceDifference =
              ((dayData.price - originalPrice) / originalPrice) * 100;
          } else {
            element.ratePlan.ratePlanData.push({
              date: a.date,
              price: a.price,
            } as ContingentRatePlanDataView);
          }
        }
      }
    }
  }
}
